import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Base_URL } from "../url";
import "../Styling/PayrollTheme.css";
import "../Styling/VIMTheme.css";

// ── Helpers ───────────────────────────────────────────────────────────────
const toDateInput = (val) => {
  if (!val) return "";
  return new Date(val).toISOString().split("T")[0];
};

const emptyItem = () => ({
  Description: "",
  HSMCode: "",
  Quantity: "",
  UnitPrice: "",
  Amount: "",
  Tax: "",
});

// ── Component ─────────────────────────────────────────────────────────────
const VIMEditInvoicePage = () => {
  const { guid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [error,   setError]     = useState(null);
  const [success, setSuccess]   = useState(null);

  // Header form state
  const [header, setHeader] = useState({
    VendorName:      "",
    VendorAddress:   "",
    VendorTaxId:     "",
    CustomerName:    "",
    CustomerAddress: "",
    CustomerTaxId:   "",
    InvoiceNumber:   "",
    InvoiceDate:     "",
    DueDate:         "",
    PaymentTerm:     "",
    PurchaseOrder:   "",
    SubTotal:        "",
    TotalTax:        "",
    InvoiceTotal:    "",
    CurrencyCode:    "",
    CGSTRate:        "",
    CGSTAmount:      "",
    SGSTRate:        "",
    SGSTAmount:      "",
    IGSTRate:        "",
    IGSTAmount:      "",
  });

  // Line items state
  const [items, setItems] = useState([emptyItem()]);

  // ── Load existing data ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch header
        const hRes = await fetch(`${Base_URL}/VIM_Masters/headers`);
        const allHeaders = await hRes.json();
        const row = (Array.isArray(allHeaders) ? allHeaders : []).find(
          (r) => r.Invoice_Staging_Guid === guid
        );

        if (!row) {
          setError("Invoice not found.");
          return;
        }

        setHeader({
          VendorName:      row.VendorName      ?? "",
          VendorAddress:   row.VendorAddress   ?? "",
          VendorTaxId:     row.VendorTaxId     ?? "",
          CustomerName:    row.CustomerName    ?? "",
          CustomerAddress: row.CustomerAddress ?? "",
          CustomerTaxId:   row.CustomerTaxId   ?? "",
          InvoiceNumber:   row.InvoiceNumber   ?? "",
          InvoiceDate:     toDateInput(row.InvoiceDate),
          DueDate:         toDateInput(row.DueDate),
          PaymentTerm:     row.PaymentTerm     ?? "",
          PurchaseOrder:   row.PurchaseOrder   ?? "",
          SubTotal:        row.BaseAmount      ?? "",
          TotalTax:        row.TotalTax        ?? "",
          InvoiceTotal:    row.InvoiceTotal    ?? "",
          CurrencyCode:    row.CurrencyCode    ?? "",
          CGSTRate:        row.CGSTRate        ?? "",
          CGSTAmount:      row.CGSTAmount      ?? "",
          SGSTRate:        row.SGSTRate        ?? "",
          SGSTAmount:      row.SGSTAmount      ?? "",
          IGSTRate:        row.IGSTRate        ?? "",
          IGSTAmount:      row.IGSTAmount      ?? "",
        });

        // Fetch items
        const iRes = await fetch(`${Base_URL}/VIM_Masters/items/${guid}`);
        const itemData = await iRes.json();

        if (Array.isArray(itemData) && itemData.length > 0) {
          setItems(
            itemData.map((i) => ({
              Description: i.Description ?? "",
              HSMCode:     i.HSMCode     ?? "",
              Quantity:    i.Quantity    ?? "",
              UnitPrice:   i.UnitPrice   ?? "",
              Amount:      i.Amount      ?? "",
              Tax:         "",           // not in VIMItemDto — leave blank
            }))
          );
        }
      } catch (err) {
        setError("Failed to load invoice data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (guid) fetchData();
  }, [guid]);

  // ── Header change handler ──────────────────────────────────────────────
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
  };

  // ── Item change handler ────────────────────────────────────────────────
  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (index) => {
    if (items.length === 1) return; // keep at least one row
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        Invoice_Staging_Guid: guid,

        VendorName:      header.VendorName,
        VendorAddress:   header.VendorAddress,
        VendorTaxId:     header.VendorTaxId,

        CustomerName:    header.CustomerName,
        CustomerAddress: header.CustomerAddress,
        CustomerTaxId:   header.CustomerTaxId,

        InvoiceNumber:   header.InvoiceNumber,
        InvoiceDate:     header.InvoiceDate   || null,
        DueDate:         header.DueDate       || null,
        PaymentTerm:     header.PaymentTerm,
        PurchaseOrder:   header.PurchaseOrder,

        SubTotal:     parseFloat(header.SubTotal)     || 0,
        TotalTax:     parseFloat(header.TotalTax)     || 0,
        InvoiceTotal: parseFloat(header.InvoiceTotal) || 0,
        CurrencyCode: header.CurrencyCode,

        CGSTRate:   parseFloat(header.CGSTRate)   || 0,
        CGSTAmount: parseFloat(header.CGSTAmount) || 0,
        SGSTRate:   parseFloat(header.SGSTRate)   || 0,
        SGSTAmount: parseFloat(header.SGSTAmount) || 0,
        IGSTRate:   parseFloat(header.IGSTRate)   || 0,
        IGSTAmount: parseFloat(header.IGSTAmount) || 0,

        ChangedBy: "user", // replace with auth user if available

        LineItems: items.map((i) => ({
          Description: i.Description,
          HSMCode:     i.HSMCode,
          Quantity:    parseFloat(i.Quantity)  || 0,
          UnitPrice:   parseFloat(i.UnitPrice) || 0,
          Amount:      parseFloat(i.Amount)    || 0,
          Tax:         parseFloat(i.Tax)       || 0,
        })),
      };

      const res = await fetch(`${Base_URL}/VIM_Masters/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = await res.text();
        setError(`Save failed: ${msg}`);
        return;
      }

      const data = await res.json();
      setSuccess("Invoice saved successfully (new version created).");

      // Navigate to the new GUID's edit page after a brief delay
      setTimeout(() => {
        navigate(`/vim-edit/${data.newInvoiceStagingGuid}`);
      }, 1500);
    } catch (err) {
      setError("An error occurred while saving.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="vim-page">
        <div className="vim-edit__loader">Loading invoice…</div>
      </div>
    );
  }

  return (
    <div className="vim-page">
      {/* ── Page header ── */}
      <div className="vim-page__header">
        <button
          className="vim-edit__back"
          onClick={() => navigate("/vim")}
        >
          ← Back
        </button>
        <h1 className="vim-page__title">Edit Invoice</h1>
        <p className="vim-page__subtitle">
          Changes will create a new version. Previous version is kept for audit.
        </p>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="vim-edit__alert vim-edit__alert--error">{error}</div>}
      {success && <div className="vim-edit__alert vim-edit__alert--success">{success}</div>}

      {/* ══════════════════════════════════════════════ */}
      {/* SECTION 1 — INVOICE HEADER                    */}
      {/* ══════════════════════════════════════════════ */}
      <div className="vim-edit__section">
        <div className="vim-edit__section-header">
          <span className="vim-edit__section-badge">1</span>
          Invoice Header
        </div>

        <div className="vim-edit__grid">
          {/* Vendor block */}
          <div className="vim-edit__block">
            <p className="vim-edit__block-label">Vendor Details</p>

            <div className="vim-edit__field">
              <label>Vendor Name</label>
              <input
                name="VendorName"
                value={header.VendorName}
                onChange={handleHeaderChange}
              />
            </div>

            <div className="vim-edit__field">
              <label>Vendor Address</label>
              <textarea
                name="VendorAddress"
                value={header.VendorAddress}
                onChange={handleHeaderChange}
                rows={3}
              />
            </div>

            <div className="vim-edit__field">
              <label>Vendor Tax ID (GSTIN)</label>
              <input
                name="VendorTaxId"
                value={header.VendorTaxId}
                onChange={handleHeaderChange}
              />
            </div>
          </div>

          {/* Customer block */}
          <div className="vim-edit__block">
            <p className="vim-edit__block-label">Customer Details</p>

            <div className="vim-edit__field">
              <label>Customer Name</label>
              <input
                name="CustomerName"
                value={header.CustomerName}
                onChange={handleHeaderChange}
              />
            </div>

            <div className="vim-edit__field">
              <label>Customer Address</label>
              <textarea
                name="CustomerAddress"
                value={header.CustomerAddress}
                onChange={handleHeaderChange}
                rows={3}
              />
            </div>

            <div className="vim-edit__field">
              <label>Customer Tax ID (GSTIN)</label>
              <input
                name="CustomerTaxId"
                value={header.CustomerTaxId}
                onChange={handleHeaderChange}
              />
            </div>
          </div>

          {/* Invoice meta block */}
          <div className="vim-edit__block">
            <p className="vim-edit__block-label">Invoice Details</p>

            <div className="vim-edit__field">
              <label>Invoice Number</label>
              <input
                name="InvoiceNumber"
                value={header.InvoiceNumber}
                onChange={handleHeaderChange}
              />
            </div>

            <div className="vim-edit__row-2">
              <div className="vim-edit__field">
                <label>Invoice Date</label>
                <input
                  type="date"
                  name="InvoiceDate"
                  value={header.InvoiceDate}
                  onChange={handleHeaderChange}
                />
              </div>

              <div className="vim-edit__field">
                <label>Due Date</label>
                <input
                  type="date"
                  name="DueDate"
                  value={header.DueDate}
                  onChange={handleHeaderChange}
                />
              </div>
            </div>

            <div className="vim-edit__row-2">
              <div className="vim-edit__field">
                <label>Payment Term</label>
                <input
                  name="PaymentTerm"
                  value={header.PaymentTerm}
                  onChange={handleHeaderChange}
                />
              </div>

              <div className="vim-edit__field">
                <label>Purchase Order</label>
                <input
                  name="PurchaseOrder"
                  value={header.PurchaseOrder}
                  onChange={handleHeaderChange}
                />
              </div>
            </div>
          </div>

          {/* Amounts block */}
          <div className="vim-edit__block">
            <p className="vim-edit__block-label">Amounts</p>

            <div className="vim-edit__row-2">
              <div className="vim-edit__field">
                <label>Sub Total</label>
                <input
                  type="number"
                  name="SubTotal"
                  value={header.SubTotal}
                  onChange={handleHeaderChange}
                />
              </div>

              <div className="vim-edit__field">
                <label>Total Tax</label>
                <input
                  type="number"
                  name="TotalTax"
                  value={header.TotalTax}
                  onChange={handleHeaderChange}
                />
              </div>
            </div>

            <div className="vim-edit__row-2">
              <div className="vim-edit__field">
                <label>Invoice Total</label>
                <input
                  type="number"
                  name="InvoiceTotal"
                  value={header.InvoiceTotal}
                  onChange={handleHeaderChange}
                />
              </div>

              <div className="vim-edit__field">
                <label>Currency</label>
                <input
                  name="CurrencyCode"
                  value={header.CurrencyCode}
                  onChange={handleHeaderChange}
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          {/* GST block */}
          <div className="vim-edit__block vim-edit__block--wide">
            <p className="vim-edit__block-label">GST Breakdown</p>

            <div className="vim-edit__gst-grid">
              <div className="vim-edit__gst-group">
                <p className="vim-edit__gst-type">CGST</p>
                <div className="vim-edit__row-2">
                  <div className="vim-edit__field">
                    <label>Rate (%)</label>
                    <input
                      type="number"
                      name="CGSTRate"
                      value={header.CGSTRate}
                      onChange={handleHeaderChange}
                    />
                  </div>
                  <div className="vim-edit__field">
                    <label>Amount</label>
                    <input
                      type="number"
                      name="CGSTAmount"
                      value={header.CGSTAmount}
                      onChange={handleHeaderChange}
                    />
                  </div>
                </div>
              </div>

              <div className="vim-edit__gst-group">
                <p className="vim-edit__gst-type">SGST</p>
                <div className="vim-edit__row-2">
                  <div className="vim-edit__field">
                    <label>Rate (%)</label>
                    <input
                      type="number"
                      name="SGSTRate"
                      value={header.SGSTRate}
                      onChange={handleHeaderChange}
                    />
                  </div>
                  <div className="vim-edit__field">
                    <label>Amount</label>
                    <input
                      type="number"
                      name="SGSTAmount"
                      value={header.SGSTAmount}
                      onChange={handleHeaderChange}
                    />
                  </div>
                </div>
              </div>

              <div className="vim-edit__gst-group">
                <p className="vim-edit__gst-type">IGST</p>
                <div className="vim-edit__row-2">
                  <div className="vim-edit__field">
                    <label>Rate (%)</label>
                    <input
                      type="number"
                      name="IGSTRate"
                      value={header.IGSTRate}
                      onChange={handleHeaderChange}
                    />
                  </div>
                  <div className="vim-edit__field">
                    <label>Amount</label>
                    <input
                      type="number"
                      name="IGSTAmount"
                      value={header.IGSTAmount}
                      onChange={handleHeaderChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════ */}
      {/* SECTION 2 — LINE ITEMS                        */}
      {/* ══════════════════════════════════════════════ */}
      <div className="vim-edit__section">
        <div className="vim-edit__section-header">
          <span className="vim-edit__section-badge">2</span>
          Line Items
          <button
            className="vim-edit__add-item"
            onClick={addItem}
          >
            + Add Row
          </button>
        </div>

        <div className="vim-edit__items-scroll">
          <table className="vim-edit__items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>HSN/SAC Code</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Amount</th>
                <th>Tax</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td className="vim-edit__item-num">{index + 1}</td>

                  <td>
                    <input
                      className="vim-edit__cell-input"
                      value={item.Description}
                      onChange={(e) =>
                        handleItemChange(index, "Description", e.target.value)
                      }
                      placeholder="Item description"
                    />
                  </td>

                  <td>
                    <input
                      className="vim-edit__cell-input vim-edit__cell-input--sm"
                      value={item.HSMCode}
                      onChange={(e) =>
                        handleItemChange(index, "HSMCode", e.target.value)
                      }
                      placeholder="HSN/SAC"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="vim-edit__cell-input vim-edit__cell-input--num"
                      value={item.Quantity}
                      onChange={(e) =>
                        handleItemChange(index, "Quantity", e.target.value)
                      }
                      placeholder="0"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="vim-edit__cell-input vim-edit__cell-input--num"
                      value={item.UnitPrice}
                      onChange={(e) =>
                        handleItemChange(index, "UnitPrice", e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="vim-edit__cell-input vim-edit__cell-input--num"
                      value={item.Amount}
                      onChange={(e) =>
                        handleItemChange(index, "Amount", e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="vim-edit__cell-input vim-edit__cell-input--num"
                      value={item.Tax}
                      onChange={(e) =>
                        handleItemChange(index, "Tax", e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </td>

                  <td>
                    <button
                      className="vim-edit__remove-item"
                      onClick={() => removeItem(index)}
                      title="Remove row"
                      disabled={items.length === 1}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Footer actions ── */}
      <div className="vim-edit__footer">
        <button
          className="vim-edit__cancel"
          onClick={() => navigate("/vim-invoices")}
          disabled={saving}
        >
          Cancel
        </button>

        <button
          className="vim-edit__save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default VIMEditInvoicePage;